import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';

import type { PageMeta } from '@/types/api';

import { endpoints } from './endpoints';
import { ACCOUNT_DEACTIVATED_CODE, ApiError, parseApiError, UnauthorizedError } from './errors';
import { emitForceLogout } from './force-logout';
import { baseUrlForSource, resolveSource } from './source-config';
import { tokenStore } from './token-store';

type RetryConfig = AxiosRequestConfig & { _retried?: boolean };

/** Endpoints whose own 401 means "bad input"/"no session yet", not "stale session" —
 *  never refresh-retry these. Prefix matches cover login (incl. otp send/verify) and the
 *  whole forgot-password flow (send/verify/reset). */
const AUTH_ENTRY_PATHS = [
  '/auth/tracking/login',
  '/auth/tracking/forgot-password',
  endpoints.auth.refresh,
];

function isAuthEntry(url: string | undefined): boolean {
  return !!url && AUTH_ENTRY_PATHS.some((p) => url.includes(p));
}

/** Dev-only so a production build can never route to the Postman mock. Also disabled under
 *  Vitest (`DEV` is false under `mode: test`) so unit tests keep hitting whatever `baseURL` they
 *  explicitly pass to `createApiClient` (and MSW-register against) — those tests exercise
 *  interceptor/retry logic, not source-switching, and know nothing about
 *  config/api-sources.json. Mirrors mock-http.ts's `LATENCY_ENABLED` gate. */
const SOURCE_OVERRIDE_ENABLED = import.meta.env.DEV;

/** This endpoint's configured source (config/api-sources.json) resolves to `mock-api` — `real`
 *  (the default for everything unlisted) is a no-op here, leaving the instance's own `baseURL`
 *  and credentialed-cookie behavior in place. */
function isMockSource(path: string | undefined): boolean {
  return SOURCE_OVERRIDE_ENABLED && !!path && resolveSource(path) === 'mock-api';
}

/**
 * The server's wording for a failed refresh, or `undefined` when there is none to quote.
 *
 * Only a message the *server* sent is returned: the local guards ("No refresh token") are this
 * module's own strings and would read as nonsense in a toast.
 */
function refreshErrorMessage(error: unknown): string | undefined {
  const response = (error as AxiosError | undefined)?.response;
  if (!response) return undefined;
  const parsed = parseApiError(response.status, response.data, '');
  return parsed.message || undefined;
}

/** A file download (`getFile`) asks for `responseType: 'blob'`, and axios applies that to the
 *  **error** body too, so an ADR-0008 envelope arrives as a Blob that `parseApiError` cannot see
 *  into — the code, the message and the `ACCOUNT_DEACTIVATED` force-logout were all lost. Ported
 *  from greenway_admin. A body that is not JSON is handed back untouched. */
async function readErrorBody(data: unknown): Promise<unknown> {
  if (!(data instanceof Blob)) return data;
  try {
    return JSON.parse(await data.text()) as unknown;
  } catch {
    return data;
  }
}

export function createApiClient(baseURL: string): AxiosInstance {
  const instance = axios.create({ baseURL, withCredentials: true });
  let refreshInFlight: Promise<void> | null = null;

  instance.interceptors.request.use((config) => {
    if (isMockSource(config.url)) {
      config.baseURL = baseUrlForSource('mock-api');
      // The Postman mock returns Access-Control-Allow-Origin: * — browsers reject that combined
      // with withCredentials (no real session cookie to exchange with a mock anyway).
      config.withCredentials = false;
    } else {
      config.baseURL = baseURL;
    }
    const token = tokenStore.getAccessToken();
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
  });

  /**
   * Spend the refresh token for a new pair.
   *
   * The contract (probed live 2026-09-02, and the one Tracking Mobile runs against): the token
   * goes in the **body** as `{ refreshToken }`, and the reply is a camelCase pair inside the
   * `{ data }` envelope — `{ data: { accessToken, refreshToken } }`. It is **rotating and
   * single-use**: each success spends the token it was given, so a spent one comes back
   * `401 INVALID_TOKEN`.
   *
   * This previously posted a `null` body and read `res.data.access_token`, which was wrong three
   * times over — no token to validate (every call answered `400 VALIDATION_FAILED`), the wrong
   * case, and one envelope level too shallow. It was written against ADR-0005's assumption that
   * the refresh token would be an HttpOnly cookie the browser replayed on its own; the deployed
   * API never worked that way, so no session could ever outlive its access token.
   *
   * `refreshInFlight` still coalesces concurrent 401s, and with rotation that is no longer just an
   * optimisation: a burst of parallel 401s each spending the token would leave every attempt after
   * the first holding a dead one, signing out a grower whose session was perfectly alive.
   */
  const doRefresh = async () => {
    if (!refreshInFlight) {
      const current = tokenStore.getRefreshToken();
      // Nothing to send is a guaranteed 400, and this endpoint is rate limited (10/s). Fail
      // straight away so the caller's 401 surfaces as the session expiry it actually is.
      if (!current) return Promise.reject(new UnauthorizedError('No refresh token'));

      const mock = isMockSource(endpoints.auth.refresh);
      const refreshBase = mock ? baseUrlForSource('mock-api') : baseURL;
      refreshInFlight = axios
        .post<{ data?: { accessToken?: string; refreshToken?: string } }>(
          `${refreshBase}${endpoints.auth.refresh}`,
          { refreshToken: current },
          { withCredentials: !mock },
        )
        .then((res) => {
          const next = res.data?.data;
          // No access token means this was not a success, whatever the status said. Reject rather
          // than half-adopting it — writing `undefined` over a working session is how a recoverable
          // expiry turns into an unrecoverable one.
          if (!next?.accessToken) throw new UnauthorizedError('Refresh returned no access token');
          tokenStore.setAccessToken(next.accessToken);
          // Rotation is the server's choice: keep the token just sent if it answered without one,
          // so a server that stops rotating does not read as a server that revoked us.
          if (next.refreshToken) tokenStore.setRefreshToken(next.refreshToken);
        })
        .finally(() => {
          refreshInFlight = null;
        });
    }
    return refreshInFlight;
  };

  instance.interceptors.response.use(
    (r) => r,
    async (error: AxiosError) => {
      const original = error.config as RetryConfig | undefined;
      if (!error.response || !original) return Promise.reject(error);
      const { status } = error.response;
      const data = await readErrorBody(error.response.data);

      if (status === 401 && !original._retried && !isAuthEntry(original.url)) {
        original._retried = true;
        try {
          await doRefresh();
        } catch (refreshError) {
          // Every request in a burst awaits the same coalesced refresh, so every one of them
          // arrives here when it rejects. The session can only die once, and the store is what
          // records that: whoever finds a credential still present is the first to notice, clears
          // it, and is the only one that announces. The rest find it already gone and stay quiet,
          // so one expiry raises one toast rather than one per in-flight request.
          const wasLive = Boolean(tokenStore.getAccessToken() ?? tokenStore.getRefreshToken());
          // Clears the refresh token as well: it was either spent, rejected or absent, so keeping
          // it would have the next 401 in the burst retry with a token already known to be dead.
          tokenStore.clear();
          // The session is over and nothing is left to recover it with, so say so and get the
          // grower back to login. Without this the app sat on a screen whose every subsequent
          // request failed, with no explanation and no way forward.
          //
          // The message is the refresh call's own — `INVALID_TOKEN`'s "The provided token is
          // invalid." and the like — because ADR-0008 says prefer what the server said. A refresh
          // that never left the client (no token to send) has nothing to quote, and <AppShell>
          // supplies the house copy for that case.
          if (wasLive) {
            emitForceLogout({ kind: 'expired', message: refreshErrorMessage(refreshError) });
          }
          return Promise.reject(new UnauthorizedError('Refresh failed', data));
        }
        return instance.request(original);
      }

      const parsed = parseApiError(status, data, error.message);
      // US-112329: a deactivated account invalidates the session immediately, on any
      // authenticated endpoint (not just login) — force the app back to the login screen.
      if (parsed.code === ACCOUNT_DEACTIVATED_CODE) {
        tokenStore.clear();
        emitForceLogout();
      }
      if (status === 401) {
        const unauthorizedError = new UnauthorizedError(parsed.message, data);
        unauthorizedError.code = parsed.code;
        unauthorizedError.details = parsed.details;
        return Promise.reject(unauthorizedError);
      }
      return Promise.reject(
        new ApiError(parsed.message, status, { code: parsed.code, details: parsed.details, data }),
      );
    },
  );

  return instance;
}

/** The deep success-side seam over an axios instance. Strips the transport wrapper
 *  (`res.data`) and the `{ data }` success envelope so no call site peels by hand;
 *  errors still propagate from the instance's interceptor as `ApiError`. Validation
 *  stays at the call site (feature schemas), so the verbs return raw `T`/`unknown`. */
export type ApiRequest = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig) => Promise<T>;
  /** List endpoints keep the `{ data, meta }` envelope — `meta` carries pagination. */
  getList: <T = unknown>(
    url: string,
    config?: AxiosRequestConfig,
  ) => Promise<{ data: T[]; meta: PageMeta }>;
  /**
   * The whole success body, envelope intact and nothing peeled.
   *
   * For endpoints whose `meta` sits **beside** `data` rather than inside it — the notifications
   * list is the one confirmed case (`{ message, data: { notifications }, meta: { nextCursor,
   * hasNextPage, unreadCount, hasUnread } }`, probed 2026-08-28). `get` would drop that `meta`
   * on the floor, taking cursor paging and the unread count with it, and `getList` cannot be
   * used because `data` here is an object, not an array.
   */
  getEnvelope: <T = unknown>(url: string, config?: AxiosRequestConfig) => Promise<T>;
  post: <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) => Promise<T>;
  put: <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) => Promise<T>;
  patch: <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) => Promise<T>;
  delete: (url: string, config?: AxiosRequestConfig) => Promise<void>;
  /**
   * A binary response with the name the server gave it. `contentDisposition` is `null` unless the
   * server sent the header **and** CORS exposes it — the caller supplies a fallback name.
   */
  getFile: (
    url: string,
    config?: AxiosRequestConfig,
  ) => Promise<{ blob: Blob; contentDisposition: string | null }>;
};

export function createApiRequest(instance: AxiosInstance): ApiRequest {
  return {
    get: async <T = unknown>(url: string, config?: AxiosRequestConfig) =>
      (await instance.get<{ data: T }>(url, config)).data.data,
    getList: async <T = unknown>(url: string, config?: AxiosRequestConfig) =>
      (await instance.get<{ data: T[]; meta: PageMeta }>(url, config)).data,
    getEnvelope: async <T = unknown>(url: string, config?: AxiosRequestConfig) =>
      (await instance.get<T>(url, config)).data,
    post: async <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
      (await instance.post<{ data: T }>(url, body, config)).data.data,
    put: async <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
      (await instance.put<{ data: T }>(url, body, config)).data.data,
    patch: async <T = unknown>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
      (await instance.patch<{ data: T }>(url, body, config)).data.data,
    delete: async (url: string, config?: AxiosRequestConfig) => {
      await instance.delete(url, config);
    },
    getFile: async (url: string, config?: AxiosRequestConfig) => {
      const res = await instance.get<Blob>(url, { ...config, responseType: 'blob' });
      const header = res.headers['content-disposition'] as string | undefined;
      return { blob: res.data, contentDisposition: header ?? null };
    },
  };
}

export const apiClient = createApiRequest(createApiClient(baseUrlForSource('real')));
